// client/src/realtime/useRealtime.ts
// React hooks for real-time subscriptions

import { useEffect, useRef, useCallback, useState } from 'react';
import { realtimeClient } from './socketClient';
import { RealtimeEventType, RealtimeEventTypeValue } from './events';

/**
 * Hook to manage WebSocket connection lifecycle
 * Call this once at the app level when user is authenticated
 */
export function useRealtimeConnection() {
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Connect when hook mounts
    realtimeClient.connect();

    const unsubConnect = realtimeClient.onConnect(() => {
      setIsConnected(true);
    });

    const unsubDisconnect = realtimeClient.onDisconnect(() => {
      setIsConnected(false);
    });

    // Set initial state
    setIsConnected(realtimeClient.isConnected());

    return () => {
      unsubConnect();
      unsubDisconnect();
      realtimeClient.disconnect();
    };
  }, []);

  return { isConnected };
}

/**
 * Subscribe to a workspace channel
 */
export function useWorkspaceChannel(workspaceId: string | null | undefined) {
  useEffect(() => {
    if (!workspaceId) return;

    const topic = `workspace:${workspaceId}`;
    realtimeClient.subscribe(topic);

    return () => {
      realtimeClient.unsubscribe(topic);
    };
  }, [workspaceId]);
}

/**
 * Subscribe to a conversation channel
 */
export function useConversationChannel(conversationId: string | null | undefined) {
  useEffect(() => {
    if (!conversationId) return;

    const topic = `conversation:${conversationId}`;
    realtimeClient.subscribe(topic);

    return () => {
      realtimeClient.unsubscribe(topic);
    };
  }, [conversationId]);
}

/**
 * Subscribe to a pipeline channel
 */
export function usePipelineChannel(pipelineId: string | null | undefined) {
  useEffect(() => {
    if (!pipelineId) return;

    const topic = `pipeline:${pipelineId}`;
    realtimeClient.subscribe(topic);

    return () => {
      realtimeClient.unsubscribe(topic);
    };
  }, [pipelineId]);
}

/**
 * Generic hook to listen for realtime events on a topic
 */
export function useRealtimeEvent<T = unknown>(
  topic: string | null | undefined,
  eventType: RealtimeEventTypeValue,
  callback: (payload: T) => void
) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!topic) return;

    const unsubscribe = realtimeClient.on<T>(
      topic,
      (payload) => callbackRef.current(payload),
      eventType
    );

    return unsubscribe;
  }, [topic, eventType]);
}

/**
 * Hook for multiple event types on a topic
 */
export function useRealtimeEvents<T = unknown>(
  topic: string | null | undefined,
  eventTypes: RealtimeEventTypeValue[],
  callback: (eventType: RealtimeEventTypeValue, payload: T) => void
) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!topic) return;

    const unsubscribes = eventTypes.map((eventType) =>
      realtimeClient.on<T>(
        topic,
        (payload) => callbackRef.current(eventType, payload),
        eventType
      )
    );

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [topic, eventTypes.join(',')]);
}

/**
 * Hook for typing indicators in a conversation
 */
export function useTypingIndicator(conversationId: string | null | undefined) {
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);

  const startTyping = useCallback(() => {
    if (!conversationId) return;

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Send typing start if not already typing
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      realtimeClient.startTyping(conversationId);
    }

    // Auto-stop after 5 seconds of no activity
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping();
    }, 5000);
  }, [conversationId]);

  const stopTyping = useCallback(() => {
    if (!conversationId) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    if (isTypingRef.current) {
      isTypingRef.current = false;
      realtimeClient.stopTyping(conversationId);
    }
  }, [conversationId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (isTypingRef.current && conversationId) {
        realtimeClient.stopTyping(conversationId);
      }
    };
  }, [conversationId]);

  return { startTyping, stopTyping };
}

/**
 * Hook to track who is typing in a conversation
 */
export function useTypingUsers(conversationId: string | null | undefined) {
  const [typingUsers, setTypingUsers] = useState<Map<string, { fullName: string; timestamp: number }>>(
    new Map()
  );

  // Listen for typing events
  useRealtimeEvent(
    conversationId ? `conversation:${conversationId}` : null,
    RealtimeEventType.CONVERSATION_TYPING,
    (payload: { userId: string; userFullName: string; isTyping: boolean }) => {
      setTypingUsers((prev) => {
        const next = new Map(prev);
        if (payload.isTyping) {
          next.set(payload.userId, {
            fullName: payload.userFullName,
            timestamp: Date.now(),
          });
        } else {
          next.delete(payload.userId);
        }
        return next;
      });
    }
  );

  // Clean up stale typing indicators (older than 10 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTypingUsers((prev) => {
        let changed = false;
        const next = new Map(prev);
        next.forEach((value, key) => {
          if (now - value.timestamp > 10000) {
            next.delete(key);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return Array.from(typingUsers.values()).map((u) => u.fullName);
}

/**
 * Hook for workspace presence
 */
export function useWorkspacePresence(workspaceId: string | null | undefined) {
  const [onlineUsers, setOnlineUsers] = useState<Array<{ id: string; fullName: string }>>([]);

  // Listen for presence updates
  useRealtimeEvent(
    workspaceId ? `workspace:${workspaceId}` : null,
    RealtimeEventType.PRESENCE_UPDATE,
    (payload: { users: Array<{ id: string; fullName: string; status: string }> }) => {
      setOnlineUsers(
        payload.users.filter((u) => u.status === 'online').map((u) => ({ id: u.id, fullName: u.fullName }))
      );
    }
  );

  // Listen for user online/offline
  useRealtimeEvent(
    workspaceId ? `workspace:${workspaceId}` : null,
    RealtimeEventType.USER_ONLINE,
    (payload: { userId: string; fullName: string }) => {
      setOnlineUsers((prev) => {
        if (prev.find((u) => u.id === payload.userId)) {
          return prev;
        }
        return [...prev, { id: payload.userId, fullName: payload.fullName }];
      });
    }
  );

  useRealtimeEvent(
    workspaceId ? `workspace:${workspaceId}` : null,
    RealtimeEventType.USER_OFFLINE,
    (payload: { userId: string }) => {
      setOnlineUsers((prev) => prev.filter((u) => u.id !== payload.userId));
    }
  );

  return onlineUsers;
}
